import { Fragment } from 'react';
import { useSetRecoilState } from 'recoil';

import { Heading } from '@/components/heading';
import { getGroupedOptions, OptionGroupInterface } from '@/components/modules/option/list/utils';
import { OptionRow } from '@/components/modules/option/row';
import { OptionModel } from '@/lib/models/option/types';
import { selectedOptionState } from '@/state/option';
import { EmissionStats } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface OptionListProps {
  options: OptionModel[];
  mode: SegmentModeType;
  emissionsStats?: EmissionStats;
  shouldShowRates?: boolean;
}

const MIN_DURATION_RATIO = 0.35;

const groupHasOptions = (group: OptionGroupInterface) => group.options.length;

const OptionList = ({ options, emissionsStats, mode, shouldShowRates = false }: OptionListProps) => {
  const optionGroups = getGroupedOptions(options, mode);
  const hasDirectOrConnecting = optionGroups[0].options.length > 0 || optionGroups[1].options.length > 0;
  const longestOptionDurationSeconds = Math.max(...options.map((option) => option.totalDurationSeconds));
  const setSelectedOption = useSetRecoilState(selectedOptionState);

  const handleClick = (option: OptionModel, index: number) => {
    trackUser.event('Open Segment Option Details', { mode, option: index });
    setSelectedOption(option);
  };

  const { headingNote, optionsWrapper } = styles();

  return (
    <div className="pb-8">
      {optionGroups.filter(groupHasOptions).map((group) => {
        const isNonConnecting = group.id === 'nonconnecting';
        return (
          <Fragment key={group.id}>
            {Boolean(!isNonConnecting || !hasDirectOrConnecting) && (
              <div>
                <Heading variant="h4" className="mb-base ml-lg mt-lg">
                  {group.title}
                  {group.subTitle && <small className={headingNote()}>{group.subTitle}</small>}
                </Heading>
                <div className={optionsWrapper()}>
                  {group.options.map((option, index) => {
                    const maxDurationRatio = Math.max(
                      MIN_DURATION_RATIO,
                      option.totalDurationSeconds / longestOptionDurationSeconds,
                    );

                    return (
                      <OptionRow
                        mode={mode}
                        key={option.id}
                        option={option}
                        emissionsStats={emissionsStats}
                        shouldShowRates={shouldShowRates}
                        onClickDetails={() => handleClick(option, index)}
                        maxDurationRatio={maxDurationRatio}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

const styles = tv({
  slots: {
    headingNote: 'block text-baseSm font-normal text-text-secondary',
    optionsWrapper: 'grid gap-2 px-4',
  },
});

export { OptionList };
